import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { UserLimitModal } from "@/components/modals/UserLimitModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateUserDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role: "user",
    is_active: true,
    is_export_user: false
  });
  
  const { handleApiError, modals, closeModal } = useUsageLimits();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/users`, formData, {
        withCredentials: true
      });
      toast.success("User created successfully");
      setFormData({ email: "", password: "", first_name: "", last_name: "", role: "user", is_active: true, is_export_user: false });
      onClose();
      onSuccess();
    } catch (error) {
      // Try to handle as usage limit error
      try {
        handleApiError(error);
      } catch (e) {
        // Not a usage limit error, handle normally
        toast.error(error.response?.data?.detail || "Failed to create user");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose} modal={true}>
      <DialogContent className="sm:max-w-[500px] z-50">
        <DialogHeader>
          <DialogTitle>Create New User</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              placeholder="user@example.com"
              required
              data-testid="create-user-email"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>First Name</Label>
              <Input
                type="text"
                value={formData.first_name}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                placeholder="John"
                data-testid="create-user-first-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Last Name</Label>
              <Input
                type="text"
                value={formData.last_name}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                placeholder="Doe"
                data-testid="create-user-last-name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Password *</Label>
            <Input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              placeholder="Minimum 8 characters"
              required
              data-testid="create-user-password"
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center space-x-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <input
              type="checkbox"
              id="is_export_user"
              checked={formData.is_export_user}
              onChange={(e) => setFormData({...formData, is_export_user: e.target.checked})}
              className="w-4 h-4 text-blue-600"
            />
            <Label htmlFor="is_export_user" className="cursor-pointer text-sm font-medium text-blue-900">
              Grant Export Permission (allows user to export leads data)
            </Label>
          </div>
          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={loading} data-testid="submit-create-user">
              {loading ? "Creating..." : "Create User"}
            </Button>
          </div>
        </form>
      </DialogContent>
      
      {/* Usage Limit Modal */}
      <UserLimitModal
        isOpen={modals.userLimit.isOpen}
        onClose={() => closeModal('userLimit')}
        limitDetails={modals.userLimit.details}
      />
    </Dialog>
  );
};

export default CreateUserDialog;
